import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import APIAxios, {APIRoutes} from '@api/axios.api';
import {AxiosError, AxiosResponse} from 'axios';
import SnackError from '@utils/error.utils';
import {getIndexationDataSource} from '@store/settings/indexation/indexation.requests';
import {setEntities} from '@ngneat/elf-entities';
import {invoicingPeriodStore} from '@store/settings/invoicingPeriod/invoicing-period.store';
import {InvoicingPeriod} from '@store/settings/invoicingPeriod/invoicing-period.model';
import {getInvoicingPeriodDataSource} from '@store/settings/invoicingPeriod/invoicing-period.requests';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';

export class InvoicingPeriodService {
  store = invoicingPeriodStore;

  resetStore = () => this.store.reset();

  getInvoicingPeriods = (): Observable<InvoicingPeriod[]> => {
    return from(APIAxios({
      ...APIRoutes.GETInvoicingPeriods(),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<InvoicingPeriod[]>) => {
          return response.data;
        }),
        tap((indexations) => {
          this.store.update(
            setEntities(indexations),
            getInvoicingPeriodDataSource.setSuccess(),
          );
        }),
        getIndexationDataSource.trackRequestStatus(),
      );
  };

  setNextPeriod() {
    return from(APIAxios({
      ...APIRoutes.POSTSetNextInvoicingPeriod()
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getInvoicingPeriods())
      );
  }

  downloadInvoicingPeriodInvoicesCsv(invoicingPeriodId: number, token) {
    return from(APIAxios({
      ...APIRoutes.GETInvoicingPeriodInvoicesCsv(invoicingPeriodId),
      params: {
        token,
      }
    }))
      .pipe(
        catchError(async (err: AxiosError) => {
          const errText = JSON.parse(await (err.response as any)?.data?.text());
          throw new SnackError(errText.message, 'error');
        }),
        tap((response: AxiosResponse<Blob>) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'execution-lines.xlsx');
        }),
      );
  }

  interfaceSupplierInvoicesSage() {
    return from(APIAxios({
      ...APIRoutes.GETInterfaceSupplierInvoicesSage(),
    }))
      .pipe(
        catchError(async (err: AxiosError) => {
          const errText = JSON.parse(await (err.response as any)?.data?.text());
          throw new SnackError(errText.message, 'error');
        }),
      );
  }

  interfaceClientInvoicesSage() {
    return from(APIAxios({
      ...APIRoutes.GETInterfaceClientInvoicesSage(),
    }))
      .pipe(
        catchError(async (err: AxiosError) => {
          const errText = JSON.parse(await (err.response as any)?.data?.text());
          throw new SnackError(errText.message, 'error');
        }),
      );
  }

  interfaceProvisionsSage() {
    return from(APIAxios({
      ...APIRoutes.GETInterfaceProvisionsSage(),
    }))
      .pipe(
        catchError(async (err: AxiosError) => {
          const errText = JSON.parse(await (err.response as any)?.data?.text());
          throw new SnackError(errText.message, 'error');
        }),
      );
  }

}

export const invoicingPeriodService = new InvoicingPeriodService();
